/**
 * Admin catalog CRUD: materials, registry property values, finishes, and finish ↔
 * material links.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type {
  AdminMaterialUpdateRequest,
  CatalogAdoptResult,
  CatalogMaterialList,
  ProcessModifiersListResponse,
} from "@/api/types";

const API = "/api/v1";

// --- Admin materials list (includes zero-pricing materials) ----------
//
// The ``useAdminPricing`` hook only knows about materials that already
// have at least one pricing row. The CreatePanel dropdowns (axis values,
// Pricing) need the full list so the admin can seed an axis value on a
// material they just created. Hence this parallel endpoint.

interface AdminMaterialsListItem {
  id: number;
  code: string;
  density_kg_m3: number;
  sort_order: number;
  is_active: boolean;
  /** The material-property registry rows. A row NAMES its own variant axis in
   *  ``variant_signature``, so readers find the values offered on a process's
   *  declared axis (``manifest.variant_param.key``) without naming an axis —
   *  see ``axisValueRows``. The property key is NOT the axis key. */
  property_values?: Array<{
    id: number;
    property_key: string;
    process_type: string;
    value_json: Record<string, unknown>;
    value_label?: string | null;
    variant_signature: Record<string, unknown>;
    variant_signature_key: string;
    is_active: boolean;
  }>;
  process_types?: string[];
  swatch_hex?: string | null;
  swatch_image_url?: string | null;
  /** H2 #4 PR-C — když `true`, Stripe paid hook rezervuje stock pro
   *  tento materiál a quote configurator zobrazuje stock_status badge.
   *  Default `false` (opt-in pro adoption-friendly upgrade). */
  tracking_mode?: string;
  inventory_managed?: boolean;
  expose_certs?: boolean;
  /** Sklad §7.1 — výchozí místo příjmu materiálu. Příjem PO bez
   *  explicitní lokace sáhne nejdřív sem, pak na výchozí místo
   *  tenanta (KAN-150). `null` = nenastaveno → „neumístěno". */
  default_putaway_location_id?: number | null;
  /** KAN-1839 — dodací lhůta materiálu (dny); `null` = bez lhůty. */
  lead_time_days?: number | null;
  /** Storage key for an admin-uploaded material datasheet PDF (one per
   *  material). `null` = nothing uploaded. Public exposure is gated by
   *  `expose_certs`. Managed via POST/DELETE /admin/materials/{id}/datasheet. */
  datasheet_url?: string | null;
  /** Per-language strings z `material_translations`. Backend vrací
   *  aktuální hodnoty pro prefill admin formu — `null` znamená že
   *  překlad pro daný jazyk dosud nebyl zadán. */
  display_name_cs?: string | null;
  display_name_en?: string | null;
  description_cs?: string | null;
  description_en?: string | null;
  features_cs?: string | null;
  features_en?: string | null;
  /** Material → category slug (`material_category` _global property). Drives the
   *  Ceník grouping by category; `null` = „bez kategorie". */
  material_category?: string | null;
}

interface AdminMaterialsListResponse {
  items: AdminMaterialsListItem[];
}

/** Verdict from ``GET /admin/materials/offer-status``. */
export interface MaterialOfferStatusRow {
  material_id: number;
  offered: boolean;
  /** Public processes the configurator can offer this material for. */
  process_types: string[];
  /** ``no_process_link`` | ``no_public_process`` | ``not_in_skill_matrix`` */
  reason: string | null;
}

export interface MaterialOfferStatusResponse {
  items: MaterialOfferStatusRow[];
}

export function useAdminMaterials(
  processType?: string | null,
  options?: Omit<
    UseQueryOptions<AdminMaterialsListResponse>,
    "queryKey" | "queryFn"
  >,
) {
  // Optional ``process_type`` query param scopes the list to a single
  // process tag. Drives per-tab Materials sections on /admin/pricing
  // so 3D filaments don't leak into the laser dropdowns.
  const qs = processType ? `?process_type=${encodeURIComponent(processType)}` : "";
  return useQuery<AdminMaterialsListResponse>({
    queryKey: ["admin", "materials", processType ?? null],
    queryFn: () =>
      apiFetch<AdminMaterialsListResponse>(`${API}/admin/materials${qs}`),
    staleTime: 10 * 1000,
    ...options,
  });
}

/**
 * Why a catalog material does (not) reach customers. The admin list is
 * ungated while ``GET /materials`` strips anything the public graph /
 * skill matrices don't cover — without this the Položky list shows
 * "Naceněno" for materials no customer can actually pick.
 */
export function useMaterialOfferStatus(
  options?: Omit<
    UseQueryOptions<MaterialOfferStatusResponse>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<MaterialOfferStatusResponse>({
    queryKey: ["admin", "material-offer-status"],
    queryFn: () =>
      apiFetch<MaterialOfferStatusResponse>(
        `${API}/admin/materials/offer-status`,
      ),
    staleTime: 10 * 1000,
    ...options,
  });
}

// --- Process modifiers — sjednocený view nad ProcessModifier řádky ---
//
// Po PR-E1/E2a vystaven endpointem ``GET /admin/modifiers?process_type=X``.
// Vrací stejný shape pro všechny typy modifikátorů.

// --- Admin CREATE — materials / axis values / pricing rows ------------
//
// These three small mutations were the missing half of the pricing-
// matrix admin surface: the admin could EDIT existing rows but had
// to open Render's psql shell to add a new material / axis value /
// pricing cell. Now every CREATE is a single-endpoint call with
// proper validation + audit-log writes.

interface AdminCreateMaterialRequest {
  /** Optional — omitted by the admin form. The code is the immutable
   *  internal join key (frozen into order + invoice snapshots), so the API
   *  derives it from the display name instead of making the admin invent
   *  one. Callers that own a stable identifier (seeds, imports) still pass
   *  it and still get a 409 on conflict. */
  code?: string;
  density_kg_m3: number;
  sort_order?: number;
  display_name_cs: string;
  display_name_en: string;
  description_cs?: string | null;
  description_en?: string | null;
  features_cs?: string | null;
  features_en?: string | null;
  /** Bod 4B — nabídku pro procesy odvozuje kategorie (proces →
   *  kategorie), takže create formulář procesy neposílá. Nepovinné
   *  pole zůstává pro callery, kteří chtějí položku rovnou zúžit
   *  explicitním allow-listem (`material_process_types`). */
  process_types?: string[];
  /** Material → category slug. Required in the admin create form so every new
   *  material carries a category (the per-category material-data schema then
   *  applies); validated server-side against the live taxonomy. */
  material_category: string;
  /** Skladová jednotka nové položky. VYNECHÁNO ⇒ server dosadí výchozí
   *  jednotku kategorie (včetně dědění od nadřazené a tenantova přepisu). */
  stock_uom?: string;
}

interface AdminCreateMaterialResponse {
  id: number;
  code: string;
  density_kg_m3: number;
  sort_order: number;
  process_types: string[];
  /** Jednotka, kterou položka opravdu dostala; ``null`` = neurčil ji nikdo. */
  stock_uom: string | null;
}

export function useAdminCreateMaterial(
  options?: Omit<
    UseMutationOptions<
      AdminCreateMaterialResponse,
      Error,
      AdminCreateMaterialRequest
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    AdminCreateMaterialResponse,
    Error,
    AdminCreateMaterialRequest
  >({
    mutationFn: (payload) =>
      apiFetch<AdminCreateMaterialResponse>(`${API}/admin/materials`, {
        method: "POST",
        body: payload,
      }),
    // Spread caller options FIRST so our onSuccess below isn't
    // silently replaced. Callers almost always pass their own
    // ``onSuccess`` (to close a modal / reset a form / flip a
    // tab) — without this ordering their callback would override
    // the cache invalidations and the UI would show stale data
    // until F5. We chain: invalidate → caller's onSuccess.
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      // Pricing list shows ``material_code`` so a new material
      // surfaces in the filter dropdown immediately. Catalog is
      // cached per-lang at 10 min — invalidate both. Admin
      // materials list drives the CreatePanel dropdowns + the
      // Materials overview table; without invalidating it, the
      // admin adds "aisi_304" and doesn't see it in the UI until
      // a page reload.
      qc.invalidateQueries({ queryKey: ["admin", "pricing"] });
      qc.invalidateQueries({ queryKey: ["admin", "materials"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

// --- Variant-axis registry values (generic over the axis) -------------
//
// One offered value of a process's declared pricing axis
// (``manifest.variant_param``) — nothing here names any axis.
//
// ``propertyId`` is the MaterialPropertyDefinition backing the axis, resolved
// from ``variant_param.property_key`` via the tenant registry
// (``useTenantMaterialPropertyRegistry``). It is NOT the axis key — the two
// differ by convention, which is why the
// process declares the link instead of anyone deriving it.

/** Material-level ``_global`` scope sentinel (mirror BE
 *  ``GLOBAL_PROCESS_TYPE`` in ``app/services/material_properties.py``). */
const GLOBAL_PROCESS_TYPE = "_global";

interface AdminCreateAxisValueVars {
  materialId: number;
  /** MaterialPropertyDefinition id backing the axis. */
  propertyId: number;
  /** Axis key from ``variant_param.key``. */
  axisKey: string;
  /** Number for a measured axis, string for an enum/label axis — the
   *  DECLARED axis type decides (``variant_params[].type``); the BE keys
   *  the row off the declaration either way. */
  value: number | string;
}

interface AdminAxisValueResponse {
  id: number;
  property_key: string;
  value_json: Record<string, unknown>;
  variant_signature: Record<string, unknown>;
  variant_signature_key: string;
  is_active: boolean;
}

function invalidateCatalog(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: ["catalog"] });
  qc.invalidateQueries({ queryKey: ["admin", "pricing"] });
  qc.invalidateQueries({ queryKey: ["admin", "materials"] });
  qc.invalidateQueries({ queryKey: ["admin", "readiness"] });
}

export function useAdminCreateAxisValue(
  options?: Omit<
    UseMutationOptions<AdminAxisValueResponse, Error, AdminCreateAxisValueVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AdminAxisValueResponse, Error, AdminCreateAxisValueVars>({
    mutationFn: ({ materialId, propertyId, axisKey, value }) =>
      apiFetch<AdminAxisValueResponse>(
        `${API}/admin/materials/${materialId}/property-values`,
        {
          method: "POST",
          body: {
            property_id: propertyId,
            // Axis values are material-level, not per-process — the BE
            // request model requires the ``_global`` scope sentinel.
            process_type: GLOBAL_PROCESS_TYPE,
            // The BE derives the signature from the DECLARED axis type
            // (compact for numeric, canonical JSON for string axes).
            variant_values: { [axisKey]: value },
            value_json:
              typeof value === "number" ? { number: value } : { string: value },
          },
        },
      ),
    // See ``useAdminCreateMaterial`` — same options-ordering rule.
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      invalidateCatalog(qc);
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

interface AdminUpdateAxisValueVars {
  materialId: number;
  valueId: number;
  isActive: boolean;
}

export function useAdminUpdateAxisValue(
  options?: Omit<
    UseMutationOptions<AdminAxisValueResponse, Error, AdminUpdateAxisValueVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AdminAxisValueResponse, Error, AdminUpdateAxisValueVars>({
    mutationFn: ({ materialId, valueId, isActive }) =>
      apiFetch<AdminAxisValueResponse>(
        `${API}/admin/materials/${materialId}/property-values/${valueId}`,
        { method: "PATCH", body: { is_active: isActive } },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      invalidateCatalog(qc);
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

interface AdminDeleteAxisValueVars {
  materialId: number;
  valueId: number;
}

export function useAdminDeleteAxisValue(
  options?: Omit<
    UseMutationOptions<void, Error, AdminDeleteAxisValueVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, AdminDeleteAxisValueVars>({
    mutationFn: ({ materialId, valueId }) =>
      apiFetch<void>(
        `${API}/admin/materials/${materialId}/property-values/${valueId}`,
        { method: "DELETE" },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      invalidateCatalog(qc);
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

interface AdminMaterialPropertyValueRow {
  id: number;
  property_key: string;
  process_type: string;
  value_json: Record<string, unknown>;
  value_label?: string | null;
  variant_signature: Record<string, unknown>;
  variant_signature_key: string;
  is_active: boolean;
}

interface AdminCreateMaterialPropertyValueVars {
  materialId: number;
  payload: {
    property_id: number;
    process_type: string;
    value_json: Record<string, unknown>;
    /** Hodnoty os, které si volající razí sám (číselná osa, výčet). */
    variant_values?: Record<string, unknown>;
    /** Osa, na kterou hodnota patří, pro volající, kterým kód razí server
     *  (option-source vlastnosti bez ``code`` ve ``value_json``) — sami
     *  ``variant_values`` vyplnit nemůžou, kód JE hodnota osy. */
    variant_axis_key?: string;
    value_label?: string | null;
    is_active?: boolean;
    sort_order?: number;
  };
}

export function useAdminCreateMaterialPropertyValue(
  options?: Omit<
    UseMutationOptions<
      AdminMaterialPropertyValueRow,
      Error,
      AdminCreateMaterialPropertyValueVars
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    AdminMaterialPropertyValueRow,
    Error,
    AdminCreateMaterialPropertyValueVars
  >({
    mutationFn: ({ materialId, payload }) =>
      apiFetch<AdminMaterialPropertyValueRow>(
        `${API}/admin/materials/${materialId}/property-values`,
        { method: "POST", body: payload },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ["admin", "materials"] });
      qc.invalidateQueries({ queryKey: ["admin", "pricing"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

interface AdminUpdateMaterialPropertyValueVars {
  materialId: number;
  valueId: number;
  patch: {
    value_json?: Record<string, unknown>;
    variant_values?: Record<string, unknown>;
    value_label?: string | null;
    is_active?: boolean;
    sort_order?: number;
  };
}

export function useAdminUpdateMaterialPropertyValue(
  options?: Omit<
    UseMutationOptions<
      AdminMaterialPropertyValueRow,
      Error,
      AdminUpdateMaterialPropertyValueVars
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    AdminMaterialPropertyValueRow,
    Error,
    AdminUpdateMaterialPropertyValueVars
  >({
    mutationFn: ({ materialId, valueId, patch }) =>
      apiFetch<AdminMaterialPropertyValueRow>(
        `${API}/admin/materials/${materialId}/property-values/${valueId}`,
        { method: "PATCH", body: patch },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ["admin", "materials"] });
      qc.invalidateQueries({ queryKey: ["admin", "pricing"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

// --- Admin finishes CRUD ---------------------------------------------
//
// Finishes = surface treatments (natural / brushed / powder / …) — one
// instance of the generic process-modifier catalog. All requests flow
// through the process-agnostic ``/admin/modifiers`` endpoints with
// ``process_type='surface_finishes'``; there is no finish-specific router.
// The admin configures:
//   1. which finishes exist (GET/POST/PATCH /admin/modifiers),
//   2. the per-finish surcharge (PATCH /admin/modifiers/{id}),
//   3. which materials each finish is offered on (POST/DELETE
//      /admin/materials/{mid}/modifiers[/{fid}]).
//
// Same "options first, then chain" invalidation pattern as the
// material/axis/pricing hooks — every admin form passes its
// own ``onSuccess`` to close the inline editor.

const SURFACE_FINISHES = "surface_finishes";

export interface AdminFinishRow {
  id: number;
  code: string;
  is_active: boolean;
  /** In what `amount` is expressed: `"flat"` = Kč/ks, `"pct"` = %.
   *  KAN-1026 — the row used to carry only a `surcharge_pct`, so a Kč/ks
   *  finish price was shown under a percent label. */
  modifier_kind: string;
  amount: number;
  sort_order: number;
  material_codes: string[];
  /** PR 9 — admin-managed swatch. */
  swatch_image_url?: string | null;
  icon_key?: string | null;
}

interface AdminFinishesListResponse {
  items: AdminFinishRow[];
}

export function useAdminFinishes(
  options?: Omit<
    UseQueryOptions<AdminFinishesListResponse>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<AdminFinishesListResponse>({
    queryKey: ["admin", "finishes"],
    queryFn: async () => {
      // Generic modifier list filtered to the finishing process.
      const res = await apiFetch<ProcessModifiersListResponse>(
        `${API}/admin/modifiers?process_type=${SURFACE_FINISHES}`,
      );
      return {
        items: res.items.map(
          (m): AdminFinishRow => ({
            id: m.id,
            code: m.code,
            is_active: m.is_active,
            modifier_kind: m.modifier_kind,
            amount: m.amount,
            sort_order: m.sort_order,
            material_codes: m.material_codes,
            swatch_image_url: m.swatch_image_url,
            icon_key: m.icon_key,
          }),
        ),
      };
    },
    staleTime: 10 * 1000,
    ...options,
  });
}

interface AdminCreateFinishRequest {
  code: string;
  modifier_kind?: string;
  amount?: number;
  sort_order?: number;
  display_name_cs: string;
  display_name_en: string;
}

export function useAdminCreateFinish(
  options?: Omit<
    UseMutationOptions<AdminFinishRow, Error, AdminCreateFinishRequest>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AdminFinishRow, Error, AdminCreateFinishRequest>({
    mutationFn: (payload) =>
      apiFetch<AdminFinishRow>(`${API}/admin/modifiers`, {
        method: "POST",
        body: { ...payload, process_type: SURFACE_FINISHES },
      }),
    // See ``useAdminCreateMaterial`` — same options-ordering rule.
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ["admin", "finishes"] });
      // Catalog embeds finish options per material — invalidate so
      // the customer-facing configurator picks up the new finish
      // as soon as it's linked.
      qc.invalidateQueries({ queryKey: ["catalog"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

interface AdminUpdateFinishVars {
  finishId: number;
  patch: {
    modifier_kind?: string;
    amount?: number;
    is_active?: boolean;
    sort_order?: number;
    /** PR 9 — admin-managed swatch. `null` clears the field. */
    swatch_image_url?: string | null;
    icon_key?: string | null;
  };
}

export function useAdminUpdateFinish(
  options?: Omit<
    UseMutationOptions<AdminFinishRow, Error, AdminUpdateFinishVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AdminFinishRow, Error, AdminUpdateFinishVars>({
    mutationFn: ({ finishId, patch }) =>
      apiFetch<AdminFinishRow>(`${API}/admin/modifiers/${finishId}`, {
        method: "PATCH",
        body: patch,
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ["admin", "finishes"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

interface AdminLinkFinishVars {
  materialId: number;
  finishId: number;
}

export function useAdminLinkFinishToMaterial(
  options?: Omit<
    UseMutationOptions<unknown, Error, AdminLinkFinishVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, AdminLinkFinishVars>({
    mutationFn: ({ materialId, finishId }) =>
      apiFetch(`${API}/admin/materials/${materialId}/modifiers`, {
        method: "POST",
        body: { modifier_id: finishId },
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ["admin", "finishes"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

export function useAdminUnlinkFinishFromMaterial(
  options?: Omit<
    UseMutationOptions<unknown, Error, AdminLinkFinishVars>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, AdminLinkFinishVars>({
    mutationFn: ({ materialId, finishId }) =>
      apiFetch(
        `${API}/admin/materials/${materialId}/modifiers/${finishId}`,
        { method: "DELETE" },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ["admin", "finishes"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

// --- Admin: materials / axis values / finishes — UPDATE + DELETE ----
//
// Cache invalidation: every mutation invalidates both
// ``["admin", "materials"]`` (the matrix list) and ``["catalog"]``
// (the customer-facing dropdown) so a deactivation surfaces in the
// next visitor's configurator without a deploy.

interface AdminUpdateMaterialVars {
  materialId: number;
  patch: AdminMaterialUpdateRequest;
}

export function useAdminUpdateMaterial(
  options?: Omit<
    UseMutationOptions<
      AdminMaterialsListItem,
      Error,
      AdminUpdateMaterialVars
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    AdminMaterialsListItem,
    Error,
    AdminUpdateMaterialVars
  >({
    mutationFn: ({ materialId, patch }) =>
      apiFetch<AdminMaterialsListItem>(
        `${API}/admin/materials/${materialId}`,
        { method: "PATCH", body: patch },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ["admin", "materials"] });
      qc.invalidateQueries({ queryKey: ["admin", "pricing"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

export function useAdminDeactivateMaterial(
  options?: Omit<
    UseMutationOptions<
      AdminMaterialsListItem,
      Error,
      { materialId: number; confirmDestructive?: boolean }
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    AdminMaterialsListItem,
    Error,
    { materialId: number; confirmDestructive?: boolean }
  >({
    // Server guard (program P, audit 2026-08-05): deaktivace
    // posledního nabízeného materiálu operace vrací 409
    // ``impact_confirm_required`` bez ``confirm_destructive`` —
    // caller, který dopad ukázal v dialogu, posílá ``true``.
    mutationFn: ({ materialId, confirmDestructive }) =>
      apiFetch<AdminMaterialsListItem>(
        `${API}/admin/materials/${materialId}${confirmDestructive ? "?confirm_destructive=true" : ""}`,
        { method: "DELETE" },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ["admin", "materials"] });
      qc.invalidateQueries({ queryKey: ["admin", "pricing"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

/**
 * Hard-delete (purge) a material — companion to soft-delete above.
 *
 * Errors:
 *   * 409 ``has_orders`` — at least one historical order snapshot still
 *     references the row; deletion is refused outright.
 *   * 409 ``has_blocking_refs`` — FK RESTRICT (skladové šarže, skladové
 *     řádky, řádky nákupních objednávek); ukliď je nejdřív.
 *
 * KAN-422: naceněné vazby ceníků NEJSOU chyba — jdou s materiálem
 * kaskádou (dialog je vypíše dopředu a admin je potvrdí).
 *
 * Reactivation of a soft-deleted material is via ``useAdminUpdateMaterial``
 * with ``patch.is_active = true``; no separate hook needed.
 */
export function useAdminDeleteMaterial(
  options?: Omit<UseMutationOptions<void, Error, number>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (materialId) =>
      apiFetch<void>(`${API}/admin/materials/${materialId}/permanent`, {
        method: "DELETE",
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ["admin", "materials"] });
      qc.invalidateQueries({ queryKey: ["admin", "pricing"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

export function useAdminDeactivateFinish(
  options?: Omit<
    UseMutationOptions<AdminFinishRow, Error, number>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AdminFinishRow, Error, number>({
    mutationFn: (finishId) =>
      apiFetch<AdminFinishRow>(`${API}/admin/modifiers/${finishId}`, {
        method: "DELETE",
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ["admin", "finishes"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

// --- Material label resolution ---------------------------------------
//
// ``code`` is the immutable internal join key (frozen into order and
// invoice snapshots) — it is plumbing, not a label. Admin surfaces that
// hold only a code (discount tiers, skill-matrix dimension values, module
// config lists) resolve it through here so the admin reads the name they
// gave the item. The code survives as the fallback, so a row whose
// material was deleted still renders something.

/** Label for a material row: its name, falling back to the raw code. */
export function materialLabel(m: {
  code: string;
  display_name_cs?: string | null;
  display_name_en?: string | null;
}): string {
  return m.display_name_cs || m.display_name_en || m.code;
}

/** ``code -> display name`` for the tenant's materials. */
export function useMaterialNameByCode(): (code: string | null | undefined) => string {
  const materials = useAdminMaterials();
  const byCode = new Map(
    (materials.data?.items ?? []).map((m) => [m.code, materialLabel(m)]),
  );
  return (code) => (code ? byCode.get(code) ?? code : "");
}

// --- KAN-1919 — „Nová položka → z katalogu platformy" ---------------------
//
// The tenant READS the platform catalog and copies one row into its shop;
// the copy is the onboarding wizard's own (BE ``copy_platform_material``).

export function useMaterialCatalog(
  filter: { category?: string; codes?: readonly string[] },
  options?: { enabled?: boolean },
) {
  const params = new URLSearchParams();
  if (filter.category) params.set("category", filter.category);
  for (const code of filter.codes ?? []) params.append("code", code);
  const qs = params.toString();
  return useQuery<CatalogMaterialList>({
    queryKey: ["admin", "material-catalog", filter.category ?? "", [...(filter.codes ?? [])]],
    queryFn: () =>
      apiFetch<CatalogMaterialList>(`${API}/admin/material-catalog${qs ? `?${qs}` : ""}`),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  });
}

export function useAdoptCatalogMaterial(
  options?: Omit<UseMutationOptions<CatalogAdoptResult, Error, string>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<CatalogAdoptResult, Error, string>({
    mutationFn: (code) =>
      apiFetch<CatalogAdoptResult>(
        `${API}/admin/material-catalog/${encodeURIComponent(code)}/adopt`,
        { method: "POST" },
      ),
    // See ``useAdminCreateMaterial`` — same options-ordering rule.
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      invalidateCatalog(qc);
      qc.invalidateQueries({ queryKey: ["admin", "material-catalog"] });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}
